// Cross-Window communication via BroadcastChannel – EIN Kanal ("graupner-suite") für alle Fenster der Suite.
// Used to sync state between main app window and pop-out child windows.
// Ereignisse (Nachricht = { event, payload, ts }):
//   kunden-changed, projekte-changed ...   Pop-ups (Kunden/Projekte)
//   termine-geaendert                       Termin/Aufgabe wurde gespeichert -> andere Fenster laden neu
//   fenster-frage / kalender-da / haupt-da  „Ist ein Kalender-/Hauptfenster offen?“ (Frage und Antwort)
//   kalender-planen                         Planungswunsch (Vorbelegung) an das Kalender-Fenster
//   projekt-oeffnen                         Werkbank eines Projekts im Hauptfenster öffnen
//   fenster-ok                              Empfänger hat den Wunsch übernommen

const CHANNEL_NAME = "graupner-suite";
const _supported = typeof BroadcastChannel !== "undefined";
const PREF_KEY = "ui_direct_popout";

/**
 * Send an event to all open windows (main + popups) of the same origin.
 * @param {string} event - z.B. "kunden-changed", "projekte-changed"
 * @param {any}    payload
 */
export const broadcast = (event, payload = null) => {
  if (!_supported) return;
  try {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    ch.postMessage({ event, payload, ts: Date.now() });
    // Browser puffert, daher kurzer Delay vor close
    setTimeout(() => ch.close(), 100);
  } catch { /* ignore */ }
};

import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * React-Hook: hört auf ein Broadcast-Event und ruft handler auf.
 */
export const useBroadcast = (event, handler) => {
  useEffect(() => {
    if (!_supported) return undefined;
    const ch = new BroadcastChannel(CHANNEL_NAME);
    const fn = (msg) => {
      if (msg?.data?.event === event) {
        try { handler(msg.data.payload); } catch { /* ignore */ }
      }
    };
    ch.addEventListener("message", fn);
    return () => {
      ch.removeEventListener("message", fn);
      ch.close();
    };
  }, [event, handler]);
};

/**
 * Direct-Pop-Out: öffnet eine Popup-Route in einem neuen Browser-Fenster.
 * Respektiert User-Pref `ui_direct_popout` (default: true).
 * @returns {boolean} true wenn Popup geöffnet, false wenn Pref aus oder Popup blockiert (Caller soll auf In-App-Modal fallbacken).
 */
export const openInPopup = (url, opts = {}) => {
  if (typeof window === "undefined") return false;
  // Pref check
  try {
    const pref = window.localStorage.getItem(PREF_KEY);
    if (pref === "false") return false;
  } catch { /* ignore */ }
  const w = opts.w || 980;
  const h = opts.h || 800;
  const left = (window.screenX || 0) + Math.max(0, (window.outerWidth - w) / 2);
  const top = (window.screenY || 0) + Math.max(0, (window.outerHeight - h) / 2);
  const features = `popup=yes,width=${w},height=${h},left=${Math.round(left)},top=${Math.round(top)},resizable=yes,scrollbars=yes`;
  // _blank gives a fresh window per call → multi-instance möglich (Kunde A und Kunde B parallel)
  const popup = window.open(url, "_blank", features);
  return !!popup;
};

/**
 * Liest die aktuelle Direct-Popout-Einstellung. Default = true.
 */
export const isDirectPopoutEnabled = () => {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(PREF_KEY) !== "false";
  } catch {
    return true;
  }
};

/**
 * Setzt die Direct-Popout-Einstellung.
 */
export const setDirectPopoutEnabled = (enabled) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PREF_KEY, enabled ? "true" : "false");
  } catch { /* ignore */ }
};


// ---------------------------------------------------------------------------------------------
// Termine und Aufgaben: „geändert“-Meldung und Kalender-Fenster (früher terminMeldung.js / kalenderFenster.js)
// ---------------------------------------------------------------------------------------------
const MEIN_FENSTER = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const STORAGE_KEY = "graupner-termine-meldung";

// Zentrale Meldung „Termine/Aufgaben wurden geändert“ an alle anderen offenen Fenster
export const terminGeaendert = () => {
  if (_supported) { broadcast("termine-geaendert", { von: MEIN_FENSTER }); return; }
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ von: MEIN_FENSTER, ts: Date.now() })); } catch { /* egal */ }
};

// Ruft laden() auf, wenn ein ANDERES Fenster Termine/Aufgaben geändert hat.
export const useTerminMeldung = (laden) => {
  const neuste = useRef(laden);
  neuste.current = laden;
  useEffect(() => {
    const ruf = (n) => { if (n && n.von !== MEIN_FENSTER) { try { neuste.current?.(); } catch { /* egal */ } } };
    let ch = null;
    if (_supported) {
      try {
        ch = new BroadcastChannel(CHANNEL_NAME);
        ch.onmessage = (e) => { if (e.data?.event === "termine-geaendert") ruf(e.data.payload); };
      } catch { ch = null; }
    }
    const beiStorage = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try { ruf(JSON.parse(e.newValue)); } catch { /* egal */ }
    };
    window.addEventListener("storage", beiStorage);
    return () => {
      window.removeEventListener("storage", beiStorage);
      try { ch?.close(); } catch { /* egal */ }
    };
  }, []);
};

export const KALENDER_URL = "/kalender";
export const KALENDER_FENSTERNAME = "graupner_kalender";

// Adresse des Kalender-Fensters mit Vorbelegung (wenn keins offen ist: neues Fenster/Tab)
export const kalenderAdresse = (v = {}) => {
  const q = new URLSearchParams({ planen: "1", modus: v.modus === "aufgabe" ? "aufgabe" : "termin" });
  if (v.kunde_id) q.set("kunde", v.kunde_id);
  if (v.projekt_id) q.set("projekt", v.projekt_id);
  if (v.monteure?.length) q.set("monteure", v.monteure.join(","));
  if (v.einsatz_id) q.set("einsatz", v.einsatz_id);
  if (v.ort) q.set("ort", v.ort);
  if (v.titel) q.set("titel", v.titel);
  if (v.start) { q.set("start", v.start); if (v.ende) q.set("ende", v.ende); }
  if (v.termin_id) q.set("termin", v.termin_id);       // vorhandenen Termin bearbeiten
  if (v.aufgabe_id) q.set("aufgabe", v.aufgabe_id);    // vorhandene Aufgabe bearbeiten
  return `${KALENDER_URL}?${q.toString()}`;
};
export const werkbankAdresse = (kunde_id, projekt_id) => `/module/projekte/werkbank/${kunde_id}${projekt_id ? `?projekt_id=${projekt_id}` : ""}`;

// Sender: Wunsch an das (zuletzt aktive) Fenster der Rolle schicken. true = ein Fenster hat ihn übernommen.
// rolle: "kalender" (Kalender-Fenster), "haupt" (Suite-Hauptfenster mit Seitenmenü), "beleg" (offener Beleg-Editor), "bausteine" (Bausteine-Fenster)
// Ereignis des Wunsches je Rolle: kalender = Planung, haupt = Projekt öffnen, beleg = Baustein einfügen, bausteine = Hinweis „schon offen“
const WUNSCH = { kalender: "kalender-planen", haupt: "projekt-oeffnen", beleg: "baustein-einfuegen", bausteine: "bausteine-hinweis", vorlagen: "vorlagen-hinweis" };
const anFenster = (rolle, nutzlast) => new Promise((fertig) => {
  if (!_supported) { fertig(false); return; }
  let ch;
  try { ch = new BroadcastChannel(CHANNEL_NAME); } catch { fertig(false); return; }
  const id = `${MEIN_FENSTER}-${Date.now()}`;
  const senden = (event, payload) => ch.postMessage({ event, payload, ts: Date.now() });
  const antworten = [];
  let beendet = false, zugesagt = false, entschieden = false;
  const ende = (ok) => { if (beendet) return; beendet = true; try { ch.close(); } catch { /* egal */ } fertig(ok); };
  const entscheide = () => {
    if (entschieden || beendet) return;
    entschieden = true;
    if (!antworten.length) { ende(false); return; }
    const bester = antworten.reduce((x, y) => (y.aktiv > x.aktiv ? y : x));   // zuletzt aktives Fenster
    senden(WUNSCH[rolle], { id, ziel: bester.von, nutzlast });
    setTimeout(() => ende(zugesagt), 700);
  };
  ch.onmessage = (e) => {
    const { event, payload: m } = e.data || {};
    if (!m || m.id !== id) return;
    if (event === `${rolle}-da`) { antworten.push(m); if (antworten.length === 1) setTimeout(entscheide, 80); }   // andere Fenster antworten in wenigen ms
    if (event === "fenster-ok") { zugesagt = true; ende(true); }
  };
  senden("fenster-frage", { id, rolle });
  setTimeout(entscheide, 300);   // keine Antwort in 300 ms = kein Fenster dieser Rolle offen
});

export const planungAnKalender = (vorbelegung) => anFenster("kalender", vorbelegung);
// Gegenrichtung: Projekt (Werkbank) im Suite-Hauptfenster öffnen; false = kein Hauptfenster offen
export const projektImHauptfenster = (kunde_id, projekt_id) => anFenster("haupt", { kunde_id, projekt_id });

// ---------------------------------------------------------------------------------------------
// Kalender richtet sich nach dem Arbeitsplatz (Stufe 2): EINE Entscheidungsstelle kalenderArt()
//   "fenster" = eigenes Fenster /kalender (zweiter Bildschirm), "seite" = Kalenderseite im selben Fenster (mit „← zurück“).
//   Handy/schmal (< 1024 px) oder Touch ohne Maus: immer "seite". Sonst die Einstellung dieses Geräts:
//   "automatisch" (Standard: mehrere Bildschirme -> Fenster, sonst Seite) | "fenster" | "seite". Hand geht vor.
// ---------------------------------------------------------------------------------------------
const KALENDER_ART_KEY = "ui_kalender_art";
export const KALENDER_ARTEN = [["automatisch", "automatisch"], ["fenster", "eigenes Fenster"], ["seite", "im Hauptfenster"]];
export const getKalenderEinstellung = () => {
  try { const v = window.localStorage.getItem(KALENDER_ART_KEY); return ["fenster", "seite"].includes(v) ? v : "automatisch"; } catch { return "automatisch"; }
};
export const setKalenderEinstellung = (v) => { try { window.localStorage.setItem(KALENDER_ART_KEY, v); } catch { /* egal */ } };
const schmalOderTouch = () => {
  if (typeof window === "undefined") return false;
  if (window.innerWidth < 1024) return true;
  try { return !!window.matchMedia && window.matchMedia("(any-pointer: coarse)").matches && !window.matchMedia("(any-pointer: fine)").matches; } catch { return false; }
};
// Was „automatisch“ gerade erkennt (Text für die Einstellungen)
export const kalenderErkannt = () => {
  if (schmalOderTouch()) return { art: "seite", text: "Erkannt: kleiner Bildschirm oder Handy → im Hauptfenster" };
  const ext = typeof window !== "undefined" ? window.screen?.isExtended : undefined;
  if (ext === true) return { art: "fenster", text: "Erkannt: mehrere Bildschirme → eigenes Fenster" };
  return { art: "seite", text: `Erkannt: 1 Bildschirm${ext === undefined ? " (dieser Browser meldet keine weiteren)" : ""} → im Hauptfenster` };
};
// Gilt für „Kalender und Bausteine auf diesem Gerät“ – dieselbe Regel, dieselbe Geräte-Einstellung
export const kalenderArt = () => {
  if (schmalOderTouch()) return "seite";
  const e = getKalenderEinstellung();
  return e === "automatisch" ? kalenderErkannt().art : e;
};

// Router-Weg für die Art „seite“ (App.js meldet useNavigate an)
let _navigator = null;
export const registriereNavigator = (fn) => { _navigator = fn; };
const seitenAdresse = (v) => `${kalenderAdresse(v).replace(KALENDER_URL, "/module/kalender")}&zurueck=1`;

// Seitenmenü „Kalender“: offenes Kalender-Fenster nach vorn, sonst nach kalenderArt() Fenster oder Seite
export const kalenderMenue = async () => {
  if (await kalenderFensterAnstupsen()) { toast.success("Kalender ist schon offen"); return; }
  if (kalenderArt() === "seite" && _navigator) { _navigator("/module/kalender"); return; }
  const f = window.open(KALENDER_URL, KALENDER_FENSTERNAME);
  if (!f) toast.warning("Der Browser hat das neue Fenster blockiert.", { duration: 15000, action: { label: "Jetzt öffnen", onClick: () => window.open(KALENDER_URL, KALENDER_FENSTERNAME) } });
};

// EINE zentrale Stelle für „+ Termin“, „+ Aufgabe“, „Termin bearbeiten“ aus allen Fenstern (Kunde, Werkbank, Mail, Wolke, Einsatz …):
// Ist ein Kalender-Fenster offen, bekommt es die Planung (egal welche Art). Sonst nach kalenderArt():
//   "fenster" -> /kalender (Fenstername graupner_kalender), "seite" -> Kalenderseite im selben Fenster mit „← zurück“.
// vorbelegung: { modus, kunde_id, projekt_id, einsatz_id, ort, titel, monteure, start, ende } oder { termin_id } / { aufgabe_id } zum Bearbeiten.
export const terminPlanen = async (vorbelegung = {}) => {
  if (await planungAnKalender(vorbelegung)) { toast.success("Im Kalender geöffnet"); return true; }
  if (kalenderArt() === "seite" && _navigator) { _navigator(seitenAdresse(vorbelegung)); return true; }
  const url = kalenderAdresse(vorbelegung);
  let fenster = null;
  try { fenster = window.open(url, KALENDER_FENSTERNAME); } catch { fenster = null; }
  if (!fenster) {
    toast.warning("Der Browser hat das neue Fenster blockiert.", {
      duration: 15000,
      action: { label: "Jetzt öffnen", onClick: () => { window.open(url, KALENDER_FENSTERNAME); } },
    });
    return false;
  }
  toast.success("Kalender geöffnet – dort geht es mit der Planung weiter");
  return true;
};

// Empfänger: antwortet auf „bist du da?“ (für seine Rolle) und nimmt Wünsche an (onNachricht(nutzlast)).
const useEmpfang = (rolle, onNachricht, aktivIst = true, infoFn = null) => {
  const neuste = useRef(onNachricht);
  neuste.current = onNachricht;
  const infoNeuste = useRef(infoFn);
  infoNeuste.current = infoFn;
  useEffect(() => {
    if (!_supported || !aktivIst) return undefined;
    let aktiv = Date.now();
    const merke = () => { aktiv = Date.now(); };
    let ch;
    try { ch = new BroadcastChannel(CHANNEL_NAME); } catch { return undefined; }
    const senden = (event, payload) => ch.postMessage({ event, payload, ts: Date.now() });
    const wunsch = WUNSCH[rolle];
    ch.onmessage = (e) => {
      const { event, payload: m } = e.data || {};
      if (event === "fenster-frage" && m?.rolle === rolle) senden(`${rolle}-da`, { id: m.id, von: MEIN_FENSTER, aktiv, info: infoNeuste.current ? infoNeuste.current() : null });
      if (event === wunsch && m?.ziel === MEIN_FENSTER) {
        merke();
        try { neuste.current?.(m.nutzlast || {}); } catch { /* egal */ }
        senden("fenster-ok", { id: m.id });
        try { window.focus(); } catch { /* egal */ }
      }
    };
    const sichtbarKurz = () => { if (document.visibilityState === "visible") merke(); };
    window.addEventListener("focus", merke);
    window.addEventListener("pointerdown", merke, true);
    window.addEventListener("keydown", merke, true);
    document.addEventListener("visibilitychange", sichtbarKurz);
    return () => {
      window.removeEventListener("focus", merke);
      window.removeEventListener("pointerdown", merke, true);
      window.removeEventListener("keydown", merke, true);
      document.removeEventListener("visibilitychange", sichtbarKurz);
      try { ch.close(); } catch { /* egal */ }
    };
  }, [rolle, aktivIst]);
};
export const useKalenderFenster = (onPlanen, aktivIst = true) => useEmpfang("kalender", onPlanen, aktivIst);   // aktivIst: nur das echte Kalender-Fenster meldet sich an
// Seitenmenü „Kalender“: ist ein Kalender-Fenster offen, bekommt es einen Hinweis (Nutzlast { hinweis: true }); true = vorhanden
export const kalenderFensterAnstupsen = () => anFenster("kalender", { hinweis: true });
export const useHauptfenster = (onProjekt) => useEmpfang("haupt", onProjekt);

// ---------------------------------------------------------------------------------------------
// Bausteine (Bausteine-Profi): eigenes Fenster /bausteine mit Vergleich; Einfügen per Kanal in den zuletzt aktiven offenen Beleg.
//   Die Entscheidung Fenster/Seite ist dieselbe wie beim Kalender (kalenderArt()); „Eigenes Fenster“ per Knopf geht immer (Hand geht vor).
// ---------------------------------------------------------------------------------------------
export const BAUSTEINE_URL = "/bausteine";
export const BAUSTEINE_FENSTERNAME = "graupner_bausteine";
export const fensterArt = kalenderArt;

// Fenster öffnen (Name fest: zweiter Klick bringt das vorhandene Fenster nach vorn)
export const bausteineFensterOeffnen = () => {
  let f = null;
  try { f = window.open(BAUSTEINE_URL, BAUSTEINE_FENSTERNAME); } catch { f = null; }
  if (!f) {
    toast.warning("Der Browser hat das neue Fenster blockiert.", { duration: 15000, action: { label: "Jetzt öffnen", onClick: () => window.open(BAUSTEINE_URL, BAUSTEINE_FENSTERNAME) } });
    return false;
  }
  try { f.focus(); } catch { /* egal */ }
  return true;
};
// Werkzeuge → Bausteine: true = Fenster ist (jetzt) offen, false = Leiste/Seite im Beleg zeigen
export const bausteineMenue = async () => {
  if (await anFenster("bausteine", { hinweis: true })) { toast.success("Bausteine ist schon offen"); return true; }
  if (kalenderArt() === "fenster") return bausteineFensterOeffnen();
  return false;
};
export const useBausteineFenster = (aktivIst = true) => useEmpfang("bausteine", () => {}, aktivIst);
// Beleg-Editor: nimmt Bausteine aus dem Fenster an. infoFn() liefert { label: "Angebot A-2026-0001" } für die Kopfzeile des Fensters.
export const useBelegEmpfang = (onBaustein, infoFn) => useEmpfang("beleg", onBaustein, true, infoFn);
// Baustein-Fenster: Baustein an den zuletzt aktiven Beleg schicken. true = angekommen
export const bausteinAnBeleg = (block, positionen) => anFenster("beleg", { block: { id: block.id, name: block.name }, positionen });
// Baustein-Fenster: welcher Beleg ist gerade offen/zuletzt aktiv? -> { label } oder null
export const aktiverBeleg = () => new Promise((fertig) => {
  if (!_supported) { fertig(null); return; }
  let ch;
  try { ch = new BroadcastChannel(CHANNEL_NAME); } catch { fertig(null); return; }
  const id = `${MEIN_FENSTER}-b-${Date.now()}`;
  const antworten = [];
  let aus = false;
  const ende = () => {
    if (aus) return; aus = true;
    try { ch.close(); } catch { /* egal */ }
    fertig(antworten.length ? antworten.reduce((x, y) => (y.aktiv > x.aktiv ? y : x)).info || null : null);
  };
  ch.onmessage = (e) => {
    const { event, payload: m } = e.data || {};
    if (event === "beleg-da" && m?.id === id) { antworten.push(m); if (antworten.length === 1) setTimeout(ende, 80); }
  };
  ch.postMessage({ event: "fenster-frage", payload: { id, rolle: "beleg" }, ts: Date.now() });
  setTimeout(ende, 300);
});

// ---------------------------------------------------------------------------------------------
// Vorlagen (01.11.2026): „Vorlage öffnen“ wie die Bausteine – eigenes, verschiebbares Fenster /vorlagen (zweiter Bildschirm).
//   Entscheidung Fenster/Beleg-Fenster = dieselbe wie bei den Bausteinen (kalenderArt()). „Übernehmen“ geht per Kanal an den
//   zuletzt aktiven Beleg (Nachricht „baustein-einfuegen“ mit Feld `vorlage`; die Baustein-Nachricht selbst bleibt unverändert).
// ---------------------------------------------------------------------------------------------
export const VORLAGEN_URL = "/vorlagen";
export const VORLAGEN_FENSTERNAME = "graupner_vorlagen";
export const vorlagenFensterOeffnen = () => {
  let f = null;
  try { f = window.open(VORLAGEN_URL, VORLAGEN_FENSTERNAME); } catch { f = null; }
  if (!f) {
    toast.warning("Der Browser hat das neue Fenster blockiert.", { duration: 15000, action: { label: "Jetzt öffnen", onClick: () => window.open(VORLAGEN_URL, VORLAGEN_FENSTERNAME) } });
    return false;
  }
  try { f.focus(); } catch { /* egal */ }
  return true;
};
// Werkzeuge → Vorlage öffnen: true = eigenes Fenster ist (jetzt) offen, false = Fenster über dem Beleg zeigen
export const vorlagenMenue = async () => {
  if (await anFenster("vorlagen", { hinweis: true })) { toast.success("Vorlagen ist schon offen"); return true; }
  if (kalenderArt() === "fenster") return vorlagenFensterOeffnen();
  return false;
};
export const useVorlagenFenster = (aktivIst = true) => useEmpfang("vorlagen", () => {}, aktivIst);
// Vorlagen-Fenster: Vorlage an den zuletzt aktiven Beleg schicken. true = angekommen (Anhängen/Ersetzen fragt der Beleg selbst)
export const vorlageAnBeleg = (tpl) => anFenster("beleg", { vorlage: { tpl } });
